interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class CoCache {
  private static instance: CoCache;
  private readonly cache: Map<string, CacheEntry<unknown>>;
  private static readonly DEFAULT_TTL = 5000;
  private static readonly MAX_CACHE_SIZE = 50;

  private constructor() {
    this.cache = new Map();
  }

  static getInstance(): CoCache {
    if (!CoCache.instance) {
      CoCache.instance = new CoCache();
    }
    return CoCache.instance;
  }

  set<T>(key: string, value: T, ttl: number = CoCache.DEFAULT_TTL): void {
    // Spec §2.2: a non-positive (0, negative) or non-finite TTL is never stored.
    if (!(ttl > 0)) {
      return;
    }
    // Remove any existing entry first so re-insertion moves the key to the
    // most-recently-used position (LRU) and updating a key never evicts another.
    this.cache.delete(key);
    if (this.cache.size >= CoCache.MAX_CACHE_SIZE) {
      this.deleteOldest();
    }
    const entry: CacheEntry<T> = { data: value, timestamp: Date.now(), ttl };
    this.cache.set(key, entry);
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    if (!entry) return null;
    if (!this.isValid(entry)) {
      this.cache.delete(key);
      return null;
    }
    // Refresh recency (LRU): move the key to the most-recently-used position.
    this.cache.delete(key);
    this.cache.set(key, entry);
    return entry.data;
  }

  clear(): void {
    this.cache.clear();
  }

  clearKey(functionIdentifier: string): void {
    for (const key of this.cache.keys()) {
      if (key.includes(functionIdentifier)) {
        this.cache.delete(key);
      }
    }
  }

  getMaxCacheSize(): number {
    return CoCache.MAX_CACHE_SIZE;
  }

  private deleteOldest(): void {
    const oldestKey = this.cache.keys().next().value;
    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  private isValid<T>(entry: CacheEntry<T>): boolean {
    return Date.now() - entry.timestamp < entry.ttl;
  }
}

export { CoCache };
export default CoCache.getInstance();
